/**
 * Main Application Component
 */

import { useState, useRef, useMemo, useEffect } from "react";
import { Box, Text, Badge, Button, Group, Modal, TextInput, Tooltip } from "@mantine/core";
import { useDrop } from "react-dnd";
import { useNavigate } from "react-router-dom";
import { IconSettings, IconPlus } from "@tabler/icons-react";
import { LeftPanel, RightPanel } from "./panels";
import { Scene, getSceneUtils } from "./scene";
import { useConnected, useInstances, useViewerStore } from "./store";
import { useWebSocket } from "./WebSocket";
import { UserMenu } from "./components";
import { initUndoSystem, recordDelete, undo, redo, useKeyboardShortcuts } from "./hooks";
import { MODULE_DRAG_TYPE } from "./components/ModuleCard";
import type { ModuleData } from "./types";

/**
 * Generate a cryptographically secure random session ID.
 * Uses crypto.getRandomValues() (128 bits of entropy).
 */
function generateSessionId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function ConnectionStatus() {
  const connected = useConnected();
  const sessionId = typeof window !== "undefined" ? window.sessionId : null;

  return (
    <Group gap="xs">
      {sessionId && (
        <Badge
          color="gray"
          variant="light"
          size="sm"
          style={{ fontFamily: "monospace" }}
        >
          Session: {sessionId}
        </Badge>
      )}
      <Badge
        color={connected ? "green" : "red"}
        variant="light"
        size="sm"
      >
        {connected ? "Connected" : "Disconnected"}
      </Badge>
    </Group>
  );
}

function Header() {
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [newSessionModalOpen, setNewSessionModalOpen] = useState(false);
  const [packageName, setPackageName] = useState("assembled_robot");
  const { sendClearAllInstances, sendExportUrdf, sendSaveConfig, sendLoadConfig } = useWebSocket();
  const instances = useInstances();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleClearAll = () => {
    sendClearAllInstances();
    setClearModalOpen(false);
  };

  const handleNewSession = () => {
    // Full reload (not client-side navigate) so the WebSocket connection
    // and in-memory store both start clean against the new session id,
    // rather than carrying over stale state from the old session.
    window.location.href = `/builder/${generateSessionId()}`;
  };

  const handleExport = () => {
    sendExportUrdf(packageName);
  };

  const handleSave = () => {
    sendSaveConfig("assembly_config");
  };

  const handleLoadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result;
        if (typeof content === "string") {
          sendLoadConfig(content);
        }
      };
      reader.readAsText(file);
      event.target.value = "";
    }
  };

  return (
    <>
      <Box
        style={{
          height: 48,
          backgroundColor: "var(--mantine-color-body)",
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
        }}
      >
        <Group gap="md">
          <Text size="md" fw={600}>
            AssemblyViewer
          </Text>
          {instances.length > 0 && (
            <>
              <TextInput
                size="xs"
                value={packageName}
                onChange={(e) => setPackageName(e.currentTarget.value)}
                placeholder="Package name"
                style={{ width: 150 }}
              />
              <Button
                size="xs"
                color="blue"
                variant="light"
                onClick={handleExport}
              >
                Export URDF
              </Button>
              <Button
                size="xs"
                variant="light"
                onClick={handleSave}
              >
                Save
              </Button>
            </>
          )}
          <Button
            size="xs"
            variant="light"
            onClick={handleLoadClick}
          >
            Load
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          {instances.length > 0 && (
            <Button
              size="xs"
              color="red"
              variant="light"
              onClick={() => setClearModalOpen(true)}
            >
              Clear All
            </Button>
          )}
        </Group>
        <Group gap="md">
          <Tooltip label="Generate MoveIt2, Gazebo, IL configs from current assembly">
            <Button
              size="xs"
              variant="light"
              color="grape"
              leftSection={<IconSettings size={14} />}
              onClick={() => navigate(`/configure?session=${window.sessionId}&package=${encodeURIComponent(packageName)}`)}
              disabled={instances.length === 0}
            >
              Config Generator
            </Button>
          </Tooltip>
          <Tooltip label="Start a fresh session with a new URL">
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => setNewSessionModalOpen(true)}
            >
              New Session
            </Button>
          </Tooltip>
          <UserMenu />
          <ConnectionStatus />
        </Group>
      </Box>

      <Modal
        opened={clearModalOpen}
        onClose={() => setClearModalOpen(false)}
        title="Clear All Modules"
        centered
        size="sm"
      >
        <Text size="sm" mb="md">
          Are you sure you want to remove all {instances.length} module(s) from the scene?
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => setClearModalOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleClearAll}>
            Clear All
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={newSessionModalOpen}
        onClose={() => setNewSessionModalOpen(false)}
        title="Start New Session"
        centered
        size="sm"
      >
        <Text size="sm" mb="md">
          This opens a new, empty session with its own URL. Your current session stays where it
          is — save or export first if you want to keep this work.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => setNewSessionModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleNewSession}>New Session</Button>
        </Group>
      </Modal>
    </>
  );
}

interface DropItem {
  module: ModuleData;
}

function ViewportDropZone() {
  const { sendAddModule } = useWebSocket();

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: MODULE_DRAG_TYPE,
    drop: (item: DropItem, monitor) => {
      const clientOffset = monitor.getClientOffset();
      let position: [number, number, number] = [0, 0, 0];

      if (clientOffset) {
        const sceneUtils = getSceneUtils();
        if (sceneUtils) {
          const groundPos = sceneUtils.getGroundPosition(clientOffset.x, clientOffset.y);
          if (groundPos) {
            position = groundPos;
          }
        }
      }

      console.log("Dropped module:", item.module.name, "at position:", position);
      sendAddModule(item.module.id, position);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  }), [sendAddModule]);

  return (
    <Box
      ref={drop}
      style={{
        flex: 1,
        position: "relative",
        overflow: "hidden",
        outline: isOver && canDrop ? "3px dashed #228be6" : "none",
        outlineOffset: "-3px",
        backgroundColor: isOver && canDrop ? "rgba(34, 139, 230, 0.05)" : "transparent",
      }}
    >
      <Scene />
      {/* Drop indicator overlay */}
      {canDrop && (
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isOver && (
            <Box
              style={{
                backgroundColor: "rgba(34, 139, 230, 0.9)",
                color: "white",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              Drop to add module
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export function App() {
  const {
    sendDeleteInstance,
    sendAddModule,
    sendSaveConfig,
    sendSelectInstance,
    sendUpdateInstancePosition,
    sendTranslationEnd,
    sendConnectModules,
    sendDisconnectInstance,
    sendJointSliderUpdate,
  } = useWebSocket();
  const instances = useInstances();

  // Initialize undo system with all senders
  useEffect(() => {
    initUndoSystem({
      sendAddModule,
      sendDeleteInstance,
      sendUpdateInstancePosition,
      sendTranslationEnd,
      sendConnectModules,
      sendDisconnectInstance,
      sendJointSliderUpdate,
    });
  }, [sendAddModule, sendDeleteInstance, sendUpdateInstancePosition, sendTranslationEnd, sendConnectModules, sendDisconnectInstance, sendJointSliderUpdate]);

  const shortcutHandlers = useMemo(
    () => ({
      onDelete: () => {
        const selected = useViewerStore.getState().selectedModuleInstanceId;
        if (!selected) return;
        const instance = instances.find((i) => i.instance_id === selected);
        if (instance) {
          recordDelete(instance, instances);
          sendDeleteInstance(selected);
        }
      },
      onDuplicate: () => {
        const state = useViewerStore.getState();
        const selected = state.selectedModuleInstanceId;
        if (!selected) return;
        const instance = instances.find((i) => i.instance_id === selected);
        if (!instance) return;
        const pos = instance.position;
        sendAddModule(instance.module_name, [pos[0] + 0.1, pos[1] + 0.1, pos[2]]);
      },
      onSave: () => {
        sendSaveConfig("assembly_config");
      },
      onUndo: () => {
        undo();
      },
      onRedo: () => {
        redo();
      },
      onDeselect: () => {
        useViewerStore.getState().setSelectedMesh(null);
        sendSelectInstance(null);
      },
    }),
    [instances, sendDeleteInstance, sendAddModule, sendSaveConfig, sendSelectInstance]
  );

  useKeyboardShortcuts(shortcutHandlers);

  return (
    <Box
      style={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Header />

      <Box
        style={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
        }}
      >
        {/* Left Panel - Modules */}
        <LeftPanel />

        {/* Center - 3D Viewport with Drop Zone */}
        <ViewportDropZone />

        {/* Right Panel - Inspector */}
        <RightPanel />
      </Box>
    </Box>
  );
}
